import { useContext, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { InfoCard, SnackBar, CustomButton } from "src/design/index";
import {
  colorBorder,
  colorTextMuted,
  colorTextPrimary,
  colorSurface,
  colorSurfaceHover,
  colorChipBackground,
  colorChipBorder,
  colorChipText,
  radiusChip,
  shadowCard,
} from "src/design/tokens";
import { RestaurantType } from "src/business/restaurant/restaurant";
import {
  useRestaurantCapabilities,
  useAttachRestaurantToCapability,
  useDetachRestaurantFromCapability,
} from "src/data/admin/queries/restaurantCapability";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";

interface Props {
  restaurant: RestaurantType;
}

export const RestaurantCapabilitiesSection = ({ restaurant }: Props) => {
  const { t } = useTranslation();
  const { refetch } = useContext(RestaurantContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: allCapabilities = [], isLoading } = useRestaurantCapabilities();

  const attachMutation = useAttachRestaurantToCapability(() => {
    refetch();
    setSnack({
      open: true,
      message: t("restaurant.capabilities.addSuccess"),
      severity: "success",
    });
  });

  const detachMutation = useDetachRestaurantFromCapability(() => {
    refetch();
    setSnack({
      open: true,
      message: t("restaurant.capabilities.removeSuccess"),
      severity: "success",
    });
  });

  const currentIds = new Set(restaurant.capabilities.map((c) => c.id));
  const unassigned = allCapabilities.filter((c) => !currentIds.has(c.id));

  const handleRemove = (capabilityId: string) => {
    detachMutation.mutate(
      { capabilityId, restaurantIds: [restaurant.id] },
      {
        onError: () =>
          setSnack({
            open: true,
            message: t("restaurant.capabilities.removeError"),
            severity: "error",
          }),
      },
    );
  };

  const handleAdd = (capabilityId: string) => {
    attachMutation.mutate(
      { capabilityId, restaurantIds: [restaurant.id] },
      {
        onError: () =>
          setSnack({
            open: true,
            message: t("restaurant.capabilities.addError"),
            severity: "error",
          }),
      },
    );
  };

  const isMutating = attachMutation.isPending || detachMutation.isPending;

  return (
    <>
      <InfoCard
        title={t("restaurant.capabilities.header")}
        subtitle={t("restaurant.capabilities.subheader")}
        icon={<TuneOutlinedIcon fontSize="small" />}
        action={
          <CustomButton
            color="inverted"
            size="small"
            startIcon={<TuneOutlinedIcon fontSize="small" />}
            onClick={() => setDrawerOpen(true)}
          >
            {t("restaurant.capabilities.manage")}
          </CustomButton>
        }
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : restaurant.capabilities.length === 0 ? (
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {t("restaurant.capabilities.noCapabilities")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {restaurant.capabilities.map((cap) => (
              <Chip
                key={cap.id}
                label={cap.name}
                size="small"
                onDelete={() => handleRemove(cap.id)}
                deleteIcon={
                  detachMutation.isPending ? (
                    <CircularProgress size={12} />
                  ) : (
                    <CloseIcon fontSize="small" />
                  )
                }
                disabled={isMutating}
                sx={{
                  backgroundColor: colorChipBackground,
                  color: colorChipText,
                  border: `1px solid ${colorChipBorder}`,
                  borderRadius: radiusChip,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            ))}
          </Box>
        )}
      </InfoCard>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ zIndex: 9999 }}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 400 },
            backgroundColor: colorSurface,
            boxShadow: shadowCard,
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${colorBorder}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              {t("restaurant.capabilities.drawer.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colorTextMuted, mt: 0.25 }}
            >
              {t("restaurant.capabilities.drawer.subtitle")}
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={() => setDrawerOpen(false)}
            sx={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: colorTextMuted,
              display: "flex",
              alignItems: "center",
              p: 0.5,
              borderRadius: "4px",
              "&:hover": {
                color: colorTextPrimary,
                backgroundColor: colorSurfaceHover,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </Box>
        </Box>

        {/* Drawer body */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : unassigned.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: colorTextMuted }}>
                {t("restaurant.capabilities.drawer.noAvailable")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {unassigned.map((cap) => (
                <ListItem
                  key={cap.id}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderBottom: `1px solid ${colorBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <ListItemText
                    primary={cap.name}
                    secondary={cap.id}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                      color: colorTextPrimary,
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      sx: { fontFamily: "monospace", color: colorTextMuted },
                    }}
                  />
                  <CustomButton
                    color="black"
                    size="small"
                    startIcon={
                      attachMutation.isPending ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <AddIcon fontSize="small" />
                      )
                    }
                    disabled={isMutating}
                    onClick={() => handleAdd(cap.id)}
                    sx={{ flexShrink: 0 }}
                  >
                    {t("restaurant.capabilities.drawer.add")}
                  </CustomButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};
